/**
 * Answer the question a blocked turn is waiting on.
 *
 * The counterpart to `interactionTools`: the agent appended the question and is
 * polling for a reply, and this is what a surface calls when a person gives
 * one. Like `cancelRun`, the whole mechanism is a log write — the surface never
 * addresses the waiting worker, which may be a daemon on another machine, and
 * may be several peers away.
 *
 * Answering twice is a no-op rather than an error. Two surfaces can legitimately
 * render the same pending question, so the second answer is a normal race, not a
 * mistake; the projection resolves it by keeping the first (see
 * `projectInteractions`). Checking here as well means the losing surface does
 * not add a pointless entry to the log.
 */

import type { AnyDocumentId, Repo } from '@automerge/automerge-repo/slim'
import type { InteractionAnswerValue } from '../../agent-tools/ask-user-question'
import { BranchLog } from '../../log/branch-log'
import { setSessionStatus } from '../../model/session/mutations'
import { SessionTaskLog } from '../../model/tasks/mutations'
import { enqueueNotification } from './enqueue-run'

export interface AnswerInteractionOptions {
  by?: string
  projectId?: string
  tasks?: SessionTaskLog
}

function taskStore(
  repo: Repo,
  opts: AnswerInteractionOptions,
): SessionTaskLog | undefined {
  return (
    opts.tasks ??
    (opts.projectId
      ? new SessionTaskLog(repo as never, undefined, opts.projectId)
      : undefined)
  )
}

interface TaskQuestion {
  taskId: string
  blocking: boolean
  resumeStatus?: 'active' | 'pending'
}

function taskQuestionFrom(bodyJson: string): TaskQuestion | null {
  try {
    const value = JSON.parse(bodyJson) as {
      taskId?: unknown
      blocking?: unknown
      resumeStatus?: unknown
    }
    const taskId = typeof value.taskId === 'string' ? value.taskId.trim() : ''
    if (!taskId) return null
    const resumeStatus =
      value.resumeStatus === 'pending' || value.resumeStatus === 'active'
        ? value.resumeStatus
        : undefined
    return {
      taskId,
      blocking: value.blocking === true,
      ...(resumeStatus ? { resumeStatus } : {}),
    }
  } catch {
    return null
  }
}

async function resumeTask(
  tasks: SessionTaskLog | undefined,
  question: TaskQuestion,
): Promise<void> {
  if (!tasks || !question.blocking) return
  if ((await tasks.get(question.taskId))?.status === 'blocked') {
    await tasks.update(question.taskId, {
      status: question.resumeStatus ?? 'active',
    })
  }
}

async function recordTaskAnswer(
  tasks: SessionTaskLog | undefined,
  question: TaskQuestion,
  sessionId: string,
  interactionId: string,
  note: string,
): Promise<void> {
  if (!tasks) return
  await tasks.recordEvent(question.taskId, {
    type: 'answered',
    at: new Date().toISOString(),
    by: sessionId,
    refs: [{ type: 'interaction', target: interactionId }],
    note,
  })
}
/**
 * Record an answer. Returns true when this call is what resolved the question,
 * false when it was already answered, cancelled, or is not on this log.
 */
export async function answerInteraction(
  repo: Repo,
  sessionId: string,
  interactionId: string,
  answers: Record<string, InteractionAnswerValue>,
  opts: AnswerInteractionOptions = {},
): Promise<boolean> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const log = new BranchLog(handle as never)

  const current = log.interactionById(interactionId)
  if (current === null || current.status !== 'pending') return false

  const taskQuestion = taskQuestionFrom(current.bodyJson)

  if (taskQuestion) {
    const tasks = taskStore(repo, opts)
    await enqueueNotification(
      repo,
      sessionId,
      (target) =>
        target.appendInteractionAnswered({
          interactionId,
          sessionId,
          outcome: 'answered',
          answerJson: JSON.stringify({ answers }),
          ...(opts.by ? { by: opts.by } : {}),
        }),
      opts.projectId ? { projectId: opts.projectId } : undefined,
    )
    await recordTaskAnswer(
      tasks,
      taskQuestion,
      sessionId,
      interactionId,
      `Answered: ${JSON.stringify(answers)}`,
    )
    await resumeTask(tasks, taskQuestion)
    await endAskingClaim(repo, sessionId, opts.projectId)
    return true
  }

  log.appendInteractionAnswered({
    interactionId,
    sessionId,
    outcome: 'answered',
    answerJson: JSON.stringify({ answers }),
    ...(opts.by ? { by: opts.by } : {}),
  })
  await endAskingClaim(repo, sessionId, opts.projectId)
  return true
}

/**
 * Dismiss a question without answering it.
 *
 * A real answer: the model is told the person declined and should proceed on
 * its best judgment, which is far better than leaving the turn to sit until the
 * timeout expires with the person staring at a form they do not want.
 */
export async function dismissInteraction(
  repo: Repo,
  sessionId: string,
  interactionId: string,
  opts: AnswerInteractionOptions = {},
): Promise<boolean> {
  const handle = await repo.find(sessionId as AnyDocumentId)
  await (handle as { whenReady?: () => Promise<void> }).whenReady?.()
  const log = new BranchLog(handle as never)

  const current = log.interactionById(interactionId)
  if (current === null || current.status !== 'pending') return false

  const taskQuestion = taskQuestionFrom(current.bodyJson)
  if (taskQuestion) {
    const tasks = taskStore(repo, opts)
    await enqueueNotification(
      repo,
      sessionId,
      (target) =>
        target.appendInteractionAnswered({
          interactionId,
          sessionId,
          outcome: 'cancelled',
          reason: 'dismissed',
          ...(opts.by ? { by: opts.by } : {}),
        }),
      opts.projectId ? { projectId: opts.projectId } : undefined,
    )
    await recordTaskAnswer(
      tasks,
      taskQuestion,
      sessionId,
      interactionId,
      'Dismissed; use best judgment.',
    )
    await resumeTask(tasks, taskQuestion)
    await endAskingClaim(repo, sessionId, opts.projectId)
    return true
  }

  log.appendInteractionAnswered({
    interactionId,
    sessionId,
    outcome: 'cancelled',
    reason: 'dismissed',
    ...(opts.by ? { by: opts.by } : {}),
  })
  await endAskingClaim(repo, sessionId, opts.projectId)
  return true
}

/**
 * An answered question ends the registry's `asking` claim (RFC 0336 S10).
 *
 * `asking` never ages out on the registry, because a question waits on a
 * person however long it takes. So the answer itself has to end the claim, or
 * a session whose runtime died while it waited would read as asking forever
 * on every surface that cannot open the session. `queued` is the honest next
 * word: the answer is in and a run is owed; a live runtime overwrites it at
 * once, and with none it ages out like any other run claim.
 *
 * Best effort. The answer is already on the log, which is what matters; a
 * registry that cannot be written to is no reason to report it unanswered.
 */
async function endAskingClaim(
  repo: Repo,
  sessionId: string,
  projectId: string | undefined,
): Promise<void> {
  try {
    const home = projectId ?? (await projectThatLists(repo, sessionId))
    if (!home) return
    await setSessionStatus(repo, home, sessionId, 'queued')
  } catch {
    // The claim stays until a runtime writes a newer one.
  }
}

/**
 * The project a session belongs to, for a caller that did not say (the CLI's
 * `session answer`). A session records the project that created it; that is
 * believed only if the project's registry still lists the session, since a
 * session can be moved and an `asking` claim written to the wrong registry
 * would be one nothing ever clears.
 */
async function projectThatLists(
  repo: Repo,
  sessionId: string,
): Promise<string | null> {
  const session = await repo.find(sessionId as AnyDocumentId)
  await (session as { whenReady?: () => Promise<void> }).whenReady?.()
  const createdBy = (session.doc() as { createdBy?: unknown } | undefined)
    ?.createdBy
  if (createdBy == null) return null
  const candidate = String(createdBy)
  const project = await repo.find(candidate as AnyDocumentId)
  await (project as { whenReady?: () => Promise<void> }).whenReady?.()
  const ids = (project.doc() as { sessionIds?: unknown[] } | undefined)
    ?.sessionIds
  return Array.isArray(ids) && ids.map(String).includes(sessionId)
    ? candidate
    : null
}
